import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  FormGroup,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  ScheduledJobConfig,
  JobUpdateBody,
  DocumentIntelligenceAnalysis,
} from '../../api/scheduledJobs';

const ALLOWED_DOMAIN = '@sozointuition.com';

interface EditJobDialogProps {
  open: boolean;
  job: ScheduledJobConfig | null;
  environment: string;
  schedulerTimezone?: string;
  onClose: () => void;
  onSave: (
    jobId: string,
    data: JobUpdateBody,
    environmentUpdate?: string
  ) => Promise<void>;
}

const EditJobDialog: React.FC<EditJobDialogProps> = ({
  open,
  job,
  environment,
  schedulerTimezone = 'Pacific (PST/PDT)',
  onClose,
  onSave,
}) => {
  const [enabled, setEnabled] = useState(true);
  const [ignoreLastRun, setIgnoreLastRun] = useState(false);
  const [scheduleTime, setScheduleTime] = useState('06:00');
  const [environmentValue, setEnvironmentValue] = useState(environment);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [analyses, setAnalyses] = useState<DocumentIntelligenceAnalysis[]>([]);
  const [backfillCorporateTransactionHistory, setBackfillCorporateTransactionHistory] =
    useState(false);

  useEffect(() => {
    if (job) {
      setEnabled(job.enabled);
      setIgnoreLastRun(job.ignore_last_run ?? false);
      setScheduleTime(job.schedule?.time || '06:00');
      setEnvironmentValue(environment);
      setEmailEnabled(job.email?.enabled ?? false);
      setRecipients(
        job.email?.recipients?.length
          ? [...job.email.recipients]
          : ['']
      );
      setRecipientError(null);
      setAnalyses(job.analyses || []);
      setBackfillCorporateTransactionHistory(
        job.analysis_options?.corporate_transactions
          ?.backfill_all_company_history ?? false
      );
    }
  }, [job, environment, open]);

  const validateRecipients = (): boolean => {
    const valid = recipients.every(
      (r) =>
        !r.trim() ||
        r.trim().toLowerCase().endsWith(ALLOWED_DOMAIN)
    );
    if (!valid) {
      setRecipientError(
        `All recipients must end with ${ALLOWED_DOMAIN}`
      );
      return false;
    }
    setRecipientError(null);
    return true;
  };

  const handleAddRecipient = () => {
    setRecipients([...recipients, '']);
  };

  const handleRemoveRecipient = (index: number) => {
    setRecipients(recipients.filter((_, i) => i !== index));
  };

  const handleRecipientChange = (index: number, value: string) => {
    const next = [...recipients];
    next[index] = value;
    setRecipients(next);
  };

  const handleSave = async () => {
    if (!job) return;
    if (!validateRecipients()) return;

    const filteredRecipients = recipients
      .map((r) => r.trim())
      .filter(Boolean);

    setSaving(true);
    try {
      const environmentUpdate =
        environmentValue !== environment ? environmentValue : undefined;
      await onSave(
        job.id,
        {
          enabled,
          ignore_last_run: ignoreLastRun,
          schedule: { time: scheduleTime },
          email: {
            enabled: emailEnabled,
            recipients: filteredRecipients,
          },
          ...(job.type === 'document_intelligence'
            ? {
                analyses,
                analysis_options: {
                  corporate_transactions: {
                    backfill_all_company_history:
                      backfillCorporateTransactionHistory,
                  },
                },
              }
            : {}),
        },
        environmentUpdate
      );
      onClose();
    } catch {
      // Error shown by parent; keep dialog open
    } finally {
      setSaving(false);
    }
  };

  if (!job) return null;

  const isIntervalGatedAgent =
    job.type === 'company_completion_agent' ||
    job.type === 'pr_coverage_gap_agent' ||
    job.type === 'latest_news_agent';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit {job.display_name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
            }
            label="Job enabled"
          />

          <FormControlLabel
            control={
              <Switch
                checked={ignoreLastRun}
                onChange={(e) => setIgnoreLastRun(e.target.checked)}
              />
            }
            label={
              isIntervalGatedAgent
                ? 'Ignore run interval (run on every daily trigger)'
                : job.type === 'document_intelligence'
                  ? 'Ignore last run time'
                  : 'Process by milestones only (ignore last run time)'
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
            {isIntervalGatedAgent
              ? 'When on: the agent runs on every daily trigger instead of waiting for the configured interval (min_interval_days). Leave off for the normal cadence.'
              : job.type === 'document_intelligence'
                ? 'When on: selected analyses use their full review windows. Leave off to respect per-analysis processed state and last-run gates.'
                : 'When on: process companies with 0 milestones or presentations newer than latest milestone. When off: also skip companies whose presentations predate the last run.'}
          </Typography>

          <FormControl size="small" fullWidth>
            <InputLabel>Environment</InputLabel>
            <Select
              value={environmentValue}
              label="Environment"
              onChange={(e) =>
                setEnvironmentValue(e.target.value as 'sandbox' | 'production')
              }
            >
              <MenuItem value="sandbox">Sandbox</MenuItem>
              <MenuItem value="production">Production</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label={`Schedule time (HH:MM, 24h ${schedulerTimezone})`}
            value={scheduleTime}
            onChange={(e) => setScheduleTime(e.target.value)}
            placeholder="06:00"
            size="small"
            helperText={`Times are in ${schedulerTimezone} (e.g. 09:00 = 9am Pacific)`}
          />

          {job.type === 'document_intelligence' && (
            <Box>
              <Typography variant="subtitle2">Analyses included</Typography>
              <Typography variant="caption" color="text.secondary">
                Milestones persist to Neo4j; pipeline writes review suggestions;
                Corporate Transactions creates review candidates and never auto-writes canonical
                transactions.
              </Typography>
              <FormGroup row sx={{ mt: 0.5 }}>
                {(
                  [
                    ['milestone', 'Milestone'],
                    ['pipeline', 'Pipeline'],
                    ['corporate_transactions', 'Corporate Transactions'],
                  ] as const
                ).map(([value, label]) => (
                  <FormControlLabel
                    key={value}
                    control={
                      <Checkbox
                        checked={analyses.includes(value)}
                        onChange={(event) =>
                          setAnalyses((current) =>
                            event.target.checked
                              ? [...current, value]
                              : current.filter((item) => item !== value)
                          )
                        }
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>
              {analyses.length === 0 && (
                <Typography variant="caption" color="error">
                  Select at least one analysis.
                </Typography>
              )}
              {analyses.includes('corporate_transactions') && (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={backfillCorporateTransactionHistory}
                        onChange={(event) =>
                          setBackfillCorporateTransactionHistory(
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Backfill all historical PRs and presentations for selected companies"
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 4, display: 'block' }}
                  >
                    Companies are still selected from the normal recent-content
                    window. Corporate Transactions then reviews their complete
                    historical PR and presentation inventory, skipping content
                    already processed with the current extractor version and
                    fingerprint.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={emailEnabled}
                onChange={(e) => setEmailEnabled(e.target.checked)}
              />
            }
            label="Email notifications enabled"
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddRecipient}
              >
                Add recipient
              </Button>
            </Box>
            {recipients.map((r, i) => (
              <Box
                key={i}
                sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder={`email${ALLOWED_DOMAIN}`}
                  value={r}
                  onChange={(e) => handleRecipientChange(i, e.target.value)}
                  error={!!recipientError && !!r && !r.toLowerCase().endsWith(ALLOWED_DOMAIN)}
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveRecipient(i)}
                  disabled={recipients.length <= 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            {recipientError && (
              <Box sx={{ color: 'error.main', fontSize: '0.875rem', mt: 0.5 }}>
                {recipientError}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || (job.type === 'document_intelligence' && analyses.length === 0)}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditJobDialog;
